import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import moment from 'moment';
import { supabase, isSupabaseConfigured, transformCalendarEventToCamelCase } from '../utils/supabase';

/**
 * Calendar events hook -- Supabase system of record (crm_calendar_events).
 *
 * 2-way Google Calendar sync happens server-side: writers just set rows
 * (inserts/updates default to sync_status='pending'), DB triggers enqueue the
 * outbox, and Cloud Functions push to Google within seconds. Inbound Google
 * changes land via webhook/poll and stream in here through Supabase Realtime.
 *
 * Deletes are SOFT (deleted_at) -- the outbox worker removes the event from
 * Google first, then hard-deletes the row (RLS blocks frontend deletes).
 *
 * Return shape is unchanged from the legacy Firestore hook: events carry
 * camelCase fields with startTime/endTime as JS Dates.
 *
 * @param {string} userId - The user whose calendar to load
 * @param {Date} visibleStart - Start of visible calendar range (default now-90d)
 * @param {Date} visibleEnd - End of visible calendar range (default now+90d)
 */
export function useCalendarEvents(userId, visibleStart = null, visibleEnd = null) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const startDate = visibleStart || moment().subtract(90, 'days').toDate();
  const endDate = visibleEnd || moment().add(90, 'days').toDate();

  // Stabilize on epoch millis so new Date objects with equal values don't re-run effects
  const startMs = useMemo(() => startDate.getTime(), [startDate]);
  const endMs = useMemo(() => endDate.getTime(), [endDate]);

  const rangeRef = useRef({ startMs, endMs });
  rangeRef.current = { startMs, endMs };

  const fetchEvents = useCallback(async () => {
    if (!userId || !isSupabaseConfigured()) {
      setEvents([]);
      setLoading(false);
      return;
    }
    try {
      const { data, error: fetchError } = await supabase
        .from('crm_calendar_events')
        .select('*')
        .eq('user_id', userId)
        .is('deleted_at', null)
        .gte('start_at', new Date(rangeRef.current.startMs).toISOString())
        .lte('start_at', new Date(rangeRef.current.endMs).toISOString())
        .order('start_at', { ascending: true });

      if (fetchError) throw fetchError;
      setEvents((data || []).map(transformCalendarEventToCamelCase));
      setError(null);
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initial + range-change fetch
  useEffect(() => {
    setLoading(true);
    fetchEvents();
  }, [fetchEvents, startMs, endMs]);

  // Realtime: reflect server-side sync (Google imports, writebacks, deletes)
  useEffect(() => {
    if (!userId || !isSupabaseConfigured()) return;

    const channel = supabase
      .channel(`calendar-events-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_calendar_events',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const { startMs: s, endMs: e } = rangeRef.current;

          if (payload.eventType === 'DELETE') {
            const oldId = payload.old?.id;
            if (oldId) setEvents((prev) => prev.filter((ev) => ev.id !== oldId));
            return;
          }

          const row = payload.new;
          if (!row) return;
          const transformed = transformCalendarEventToCamelCase(row);
          const inRange =
            transformed.startTime &&
            transformed.startTime.getTime() >= s &&
            transformed.startTime.getTime() <= e;

          setEvents((prev) => {
            const without = prev.filter((ev) => ev.id !== transformed.id);
            if (transformed.deletedAt || !inRange) return without;
            return [...without, transformed].sort((a, b) => a.startTime - b.startTime);
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  /**
   * Create a calendar event. Accepts the legacy camelCase shape.
   */
  const createEvent = async (eventData) => {
    try {
      const row = {
        user_id: userId,
        created_by: userId,
        title: eventData.title || 'Untitled Event',
        description: eventData.description || '',
        event_type: eventData.eventType || 'general',
        start_at: (eventData.startTime instanceof Date ? eventData.startTime : new Date(eventData.startTime)).toISOString(),
        end_at: (eventData.endTime instanceof Date ? eventData.endTime : new Date(eventData.endTime)).toISOString(),
        all_day: eventData.allDay || false,
        attendees: eventData.attendees || [],
        add_video_link: eventData.addVideoLink || false,
        zoom_link: eventData.zoomMeetingLink || null,
        related_entity_type: eventData.relatedTo?.type || null,
        related_entity_id: eventData.relatedTo?.id || null,
        related_entity_name: eventData.relatedTo?.name || null,
        deal_id: eventData.relatedTo?.type === 'deal' ? eventData.relatedTo.id : null,
        contact_id: eventData.relatedTo?.type === 'contact' ? eventData.relatedTo.id : null,
      };

      const { data, error: insertError } = await supabase
        .from('crm_calendar_events')
        .insert(row)
        .select('*')
        .single();
      if (insertError) throw insertError;

      // Optimistic local add (realtime will reconcile)
      const transformed = transformCalendarEventToCamelCase(data);
      setEvents((prev) => [...prev.filter((ev) => ev.id !== transformed.id), transformed]
        .sort((a, b) => a.startTime - b.startTime));

      return { success: true, id: data.id };
    } catch (err) {
      console.error('Error creating event:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  /**
   * Update an event. The DB trigger re-marks it 'pending' and the outbox
   * pushes the change to Google -- no sync bookkeeping needed here.
   */
  const updateEvent = async (eventId, updates) => {
    try {
      const row = {};
      if (updates.title !== undefined) row.title = updates.title;
      if (updates.description !== undefined) row.description = updates.description;
      if (updates.eventType !== undefined) row.event_type = updates.eventType;
      if (updates.startTime !== undefined) {
        row.start_at = (updates.startTime instanceof Date ? updates.startTime : new Date(updates.startTime)).toISOString();
      }
      if (updates.endTime !== undefined) {
        row.end_at = (updates.endTime instanceof Date ? updates.endTime : new Date(updates.endTime)).toISOString();
      }
      if (updates.allDay !== undefined) row.all_day = updates.allDay;
      if (updates.attendees !== undefined) row.attendees = updates.attendees;
      if (updates.addVideoLink !== undefined) row.add_video_link = updates.addVideoLink;
      if (updates.relatedTo !== undefined) {
        row.related_entity_type = updates.relatedTo?.type || null;
        row.related_entity_id = updates.relatedTo?.id || null;
        row.related_entity_name = updates.relatedTo?.name || null;
      }

      const { data, error: updateError } = await supabase
        .from('crm_calendar_events')
        .update(row)
        .eq('id', eventId)
        .select('*')
        .single();
      if (updateError) throw updateError;

      const transformed = transformCalendarEventToCamelCase(data);
      setEvents((prev) => prev.map((ev) => (ev.id === eventId ? transformed : ev)));

      return { success: true };
    } catch (err) {
      console.error('Error updating event:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  /**
   * Soft-delete an event; the server removes it from Google, then hard-deletes.
   */
  const deleteEvent = async (eventId) => {
    try {
      const { error: deleteError } = await supabase
        .from('crm_calendar_events')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', eventId);
      if (deleteError) throw deleteError;

      setEvents((prev) => prev.filter((ev) => ev.id !== eventId));
      return { success: true };
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  /**
   * Reschedule event (drag-and-drop support)
   */
  const rescheduleEvent = async (eventId, newStart, newEnd) => {
    return await updateEvent(eventId, {
      startTime: newStart,
      endTime: newEnd,
    });
  };

  return {
    events,
    loading,
    error,
    createEvent,
    updateEvent,
    deleteEvent,
    rescheduleEvent,
    refreshEvents: fetchEvents,
  };
}
